"use client";

import React from "react";

export interface IconStep {
  number: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

export interface IconStepsProps {
  title?: string;
  subtitle?: string;
  steps: IconStep[];
  columns?: 2 | 3 | 4;
  showConnectors?: boolean;
  bgColor?: string;
}

export function IconSteps({
  title,
  subtitle,
  steps,
  columns = 4,
  showConnectors = false,
  bgColor = "bg-white"
}: IconStepsProps) {
  const gridCols = {
    2: "md:grid-cols-2",
    3: "md:grid-cols-3",
    4: "md:grid-cols-4"
  };

  return (
    <section className={`py-16 md:py-20 ${bgColor}`}>
      <div className="max-w-6xl mx-auto px-4">
        {title && (
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {title}
            </h2>
            {subtitle && (
              <p className="text-xl text-gray-700 max-w-3xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className={`grid ${gridCols[columns]} gap-8 relative`}>
          {/* Connectors */}
          {showConnectors && (
            <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-gray-200" 
                 style={{ transform: "translateY(-50%)" }}>
              <div className="relative h-full">
                {steps.map((_, index) => (
                  index < steps.length - 1 && (
                    <div
                      key={index}
                      className="absolute h-full bg-primary-200"
                      style={{
                        left: `${(index / (steps.length - 1)) * 100}%`,
                        width: `${100 / (steps.length - 1)}%`
                      }}
                    />
                  )
                ))}
              </div>
            </div>
          )}

          {steps.map((step, index) => (
            <div key={index} className="text-center relative">
              <div className="mb-6">
                <div className="w-24 h-24 bg-primary-100 rounded-2xl flex items-center justify-center mx-auto mb-4 relative">
                  <div className="text-primary-600">
                    {step.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                    {step.number}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-700">
                  {step.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}